"use client";

import Image from "next/image";
import { Heading, Text } from "@/components/ui/typography";
import Link from "next/link";

const features = [
  "Multi-currency accounts with online dashboards",
  "Remote onboarding — no branch visit required",
  "Global payment processor compatibility",
  "Days to weeks approval timelines",
];

const cards = [
  {
    name: "Airwallex",
    desc: "Best for eCommerce, Stripe integrations, global supplier payments",
    img: "/banking-setup/image-259.svg",
    bg: "bg-[#FF833E]",
  },
  {
    name: "Statrys",
    desc: "Strong for trading companies and USD processing",
    img: "/banking-setup/image-260.svg",
    bg: "bg-[#DF543F]",
  },
  {
    name: "Wise Business",
    desc: "Consultants, agencies, SaaS founders",
    img: "/banking-setup/image-261.svg",
    bg: "bg-[#163300]",
  },
  {
    name: "Payoneer",
    desc: "Amazon sellers, freelancers, international marketplaces",
    img: "/banking-setup/image-262.svg",
    bg: "bg-[#F57200]",
  },
];

export default function SolutionSection({ heading = "Modern HK Business Banking Through Fintech and Boutique Institutions" }: { heading?: string } = {}) {
  return (
    <section className="bg-black py-[30px] md:py-[60px]  px-4 min-[1340px]:px-0">
      <div className="max-w-[1200px] mx-auto flex flex-col gap-14">
        {/* TOP CONTENT */}
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* LEFT TEXT */}
          <div className="flex flex-col gap-6">
            <span className="text-[#00A63E] text-lg sm:text-xl font-cambo">
              The Solution
            </span>

            <Heading as="h2" variant="section">
              {heading}
            </Heading>

            <Text variant="muted" muted={60} className="leading-[160%]">
              The banking landscape in Hong Kong has changed. Today, many
              international founders successfully open accounts through modern,
              digital-first institutions built for global founders.
              <br />
              <br />
              <Link href="/" className="hover:underline">
                MuteTaxes
              </Link>{" "}
              helps identify which option fits your business model and prepares
              your application to maximise approval probability — because
              choosing the wrong institution first often leads to rejection
              history, which makes the next application harder.
            </Text>
          </div>

          {/* RIGHT FEATURES */}
          <div className="flex flex-col gap-3">
            {features.map((item, index) => (
              <div
                key={index}
                className="bg-[#00A63E]/10 border-l-2 border-[#00A63E] rounded-lg px-5 py-3"
              >
                <p className="text-white text-sm sm:text-base">{item}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CARDS */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((card, index) => (
            <div
              key={index}
              className={`${card.bg} rounded-2xl p-5 flex flex-col justify-between h-[220px] relative`}
            >
              {/* CONTENT */}
              <div className="flex flex-col gap-4">
                <Image
                  src={card.img}
                  alt={card.name}
                  width={48}
                  height={48}
                  className="rounded-full"
                />

                <div>
                  <Heading as="h3" variant="cardTitleLight" className="text-lg sm:text-xl">
                    {card.name}
                  </Heading>
                  <p className="text-white/80 text-xs sm:text-sm mt-2 leading-[140%]">
                    {card.desc}
                  </p>
                </div>
              </div>

              {/* ARROW */}
              <div className="absolute bottom-4 right-4 w-7 h-7  rounded-full flex items-center justify-center">
                <Image
                  src={"/banking-setup/rotate-arrow.svg"}
                  fill
                  alt="arrow"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
